/* eslint-disable react/require-default-props */
import React, {useState} from 'react';
import {Platform, TouchableOpacity, View} from 'react-native';
import Modal from 'react-native-modal';
import {get} from 'lodash';

import Icon from '/component/base/Icon';
import RadioButtonGroup from '/component/base/RadioButton/RadioButtonGroup';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {Reports} from '/constant/TestIDs';
import {PROFILE_ORDER_HISTORY, REPORT_ALL_REFERRALS} from '/enum/records.enum';
import {EVENT_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {useCoachAnalyticsData} from '/util/referralAnalytics.util';
import {getSortOptions} from '/util/sort.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import KeyboardFocusable from '../ExtKeyboard/KeyboardFocusable';

import {styles} from './SortButtonwithArrow.style';
import {
  getSortByWithLabel,
  showReportsWithSortByLabel,
} from './SortButtonWithArrow.util';
interface SortButtonWithArrowProps {
  style?: {
    container?: any;
  };
  recordsName: string;
  sortParams: Record<string, any>;
  setSelectedSortParam: (params: any) => void;
  disabled: boolean | null;
}

const PROFILE_ORDER_HISTORY_SORT_LABEL_BY_KEY = {
  profileOrderDateDesc: 'Order date, newest to oldest',
  profileOrderDateAsc: 'Order date, oldest to newest',
  profileAmountDesc: 'Order total, high to low',
  profileAmountAsc: 'Order total, low to high',
};

const SortButtonWithArrow = ({
  style = {},
  recordsName = '',
  sortParams = {},
  setSelectedSortParam = () => {},
  disabled = null,
}: SortButtonWithArrowProps) => {
  const {tt} = useTranslation('report.allOrdersSortButton');
  const coachAnalyticsData = useCoachAnalyticsData();

  let containerStyles = [styles.container, style.container];
  if (disabled) {
    containerStyles.push(styles.disabled);
  }
  const [modalVisible, setModalVisible] = useState(false);
  const sortLabel = get(sortParams, [recordsName, 'label'], '');
  const sortKey = get(sortParams, [recordsName, 'key'], '');
  const isProfileOrderHistory = recordsName === PROFILE_ORDER_HISTORY;
  const showSortByWithLabel = showReportsWithSortByLabel.includes(recordsName);
  const selectedSortLabel = isProfileOrderHistory
    ? get(PROFILE_ORDER_HISTORY_SORT_LABEL_BY_KEY, sortKey, sortLabel)
    : tt(`sortBy-${sortKey}`, `Sort by: ${sortLabel}`);
  const openModal = () => {
    setModalVisible(true);
  };
  const closeModal = () => {
    setModalVisible(false);
  };
  const sortOptions = getSortOptions({recordsName});
  const sendSortEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.sortEvent,
  });

  const SortArray = sortOptions
    ? sortOptions.map((item: any) => ({
        text: item?.label,
        selected: item.key === get(sortParams, [recordsName, 'key'], ''),

        onPress: () => {
          setSelectedSortParam({recordsName, sortParam: item});

          // Send Tealium event for referrals
          if (recordsName === REPORT_ALL_REFERRALS) {
            const eventData = {
              sortType: item.analylicsTag,
              reportName: REPORT_ALL_REFERRALS,
              coach_highest_rank: coachAnalyticsData.coachHighestRank,
              coach_current_rank: coachAnalyticsData.coachCurrentRank,
              lastPaidRank: coachAnalyticsData.lastPaidRank,
              coach_id: coachAnalyticsData.coachId,
            };
            sendSortEvent(eventData);
          }

          closeModal();
        },

        testID: item.key,
      }))
    : [];

  return (
    <View>
      <Gutter style={containerStyles}>
        <TouchableOpacity
          {...testProperties(Reports.reportSortButton, true)}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={
            sortLabel
              ? `Sort, current selection ${sortLabel}`
              : tt('sortBy', 'Sort')
          }
          accessibilityState={{disabled: !!disabled}}
          disabled={disabled}
          onPress={openModal}
          style={styles.sortContainer}>
          {showSortByWithLabel ? (
            <>
              <Icon name="sort" style={styles.fontSize20} />
              <Text
                accessible={true}
                {...testProperties(Reports.reportSortButtonLabel)}
                style={styles.text}
                truncate
                isBold>
                {selectedSortLabel}
              </Text>
            </>
          ) : (
            <>
              <Text
                accessible={true}
                accessibilityLabel={'Sort'}
                {...testProperties(Reports.reportSortButtonLabel)}
                style={styles.text}>
                {' '}
                {tt('sortBy', 'Sort : ')}
              </Text>
              <Icon name="sort" style={styles.fontSize20} />
            </>
          )}
        </TouchableOpacity>
      </Gutter>
      <Modal
        animationIn={Platform.OS === 'web' ? 'slideInRight' : 'slideInUp'}
        animationOut={Platform.OS === 'web' ? 'slideOutRight' : 'slideOutDown'}
        style={styles.modal}
        isVisible={modalVisible}
        useNativeDriver
        hideModalContentWhileAnimating
        onBackdropPress={closeModal}>
        <View style={styles.modalView} accessible={false}>
          <View style={styles.modalHeader} accessible={false}>
            <KeyboardFocusable
              accessible={true}
              accessibilityLabel="close"
              accessibilityRole="button"
              onPress={() => {
                setModalVisible(!modalVisible);
              }}
              {...testProperties(Reports.reportSortModal, true)}>
              <Icon name="close" style={styles.closeIcon} />
            </KeyboardFocusable>
            <Text
              style={styles.headerText}
              {...testProperties(Reports.reportSortByHeader)}
              isBold
              accessible={true}
              accessibilityLabel={'Sort by'}
              accessibilityRole="header">
              {tt('sort', 'Sort by')}
            </Text>
          </View>
          <View style={styles.radioButtonsContainer} accessible={false}>
            <RadioButtonGroup
              title={getSortByWithLabel(recordsName)}
              options={SortArray}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default SortButtonWithArrow;
